import { StoreBadges } from "@/components/common/StoreBadges";
import { Fragment, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  ArrowDown,
  ArrowRight,
  Check,
  ChevronDown,
  ExternalLink,
  ShieldCheck,
} from "lucide-react";
import { useLanguage } from "@/lib/i18n";
import type {
  Comparison,
  FaqQA,
  LinkRef,
  MatrixTable,
  ReceiptExample,
  Section,
} from "@/data/content-types";

/* Parse simple inline markdown links: [label](/path) -> crawlable <Link>. */
export function InlineText({ text }: { text: string }): ReactNode {
  const parts: ReactNode[] = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const [, label, href] = match;
    if (href.startsWith("/")) {
      parts.push(
        <Link
          key={key++}
          to={href}
          className="text-primary underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors"
        >
          {label}
        </Link>,
      );
    } else {
      parts.push(
        <a
          key={key++}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors"
        >
          {label}
        </a>,
      );
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.map((p, i) => <Fragment key={i}>{p}</Fragment>);
}

export function Prose({ paragraphs }: { paragraphs?: string[] }) {
  if (!paragraphs?.length) return null;
  return (
    <>
      {paragraphs.map((p, i) => (
        <p
          key={i}
          className="text-base sm:text-lg leading-relaxed text-muted-foreground mb-4 last:mb-0"
        >
          <InlineText text={p} />
        </p>
      ))}
    </>
  );
}

export function Bullets({ items }: { items?: string[] }) {
  if (!items?.length) return null;
  return (
    <ul className="mt-4 space-y-2.5">
      {items.map((item, i) => (
        <li key={i} className="flex gap-3 text-base leading-relaxed text-foreground/85">
          <Check className="mt-1 h-4 w-4 shrink-0 text-primary" />
          <span>
            <InlineText text={item} />
          </span>
        </li>
      ))}
    </ul>
  );
}

const exampleUi = {
  fr: {
    onReceipt: "Sur le reçu",
    inRefound: "Dans ReFound",
    code: "Code",
    description: "Description imprimée",
    identified: "Produit identifié",
    warranty: "Garantie",
  },
  en: {
    onReceipt: "On the receipt",
    inRefound: "In ReFound",
    code: "Code",
    description: "Printed description",
    identified: "Identified product",
    warranty: "Warranty",
  },
};

/**
 * "On the receipt -> In ReFound" mapping for one real demo-account line.
 * Every fact is plain text in the DOM and no interaction is required, so the
 * mapping stays readable for crawlers and answer engines. The product photo,
 * warranty badge and merchant link come from the same demo record; they
 * illustrate the text, they never carry information the text does not have.
 */
export function ReceiptExampleCard({ example }: { example: ReceiptExample }) {
  const { language } = useLanguage();
  const t = exampleUi[language];

  return (
    <figure className="my-6 overflow-hidden rounded-2xl border border-border/60 bg-card/40">
      <div className="grid items-stretch gap-px bg-border/40 sm:grid-cols-[1fr_auto_1fr]">
        {/* Receipt side — simplified ticket, real printed strings only */}
        <div className="bg-muted/30 px-5 py-5">
          <span className="block text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
            {t.onReceipt}
          </span>
          <dl className="mt-3 space-y-2 font-mono text-sm">
            {example.code ? (
              <div>
                <dt className="text-[11px] uppercase tracking-wide text-muted-foreground/70">
                  {t.code}
                </dt>
                <dd className="text-foreground/90">{example.code}</dd>
              </div>
            ) : null}
            <div>
              <dt className="text-[11px] uppercase tracking-wide text-muted-foreground/70">
                {t.description}
              </dt>
              <dd className="text-foreground/90">{example.receiptLine}</dd>
            </div>
          </dl>
        </div>

        {/* Arrow: down on mobile, right on desktop */}
        <div className="flex items-center justify-center bg-card/60 px-4 py-2 sm:px-3">
          <ArrowDown className="h-4 w-4 text-primary sm:hidden" aria-hidden="true" />
          <ArrowRight className="hidden h-4 w-4 text-primary sm:block" aria-hidden="true" />
        </div>

        {/* ReFound side — what the app actually displayed */}
        <div className="bg-primary/[0.05] px-5 py-5">
          <span className="block text-[11px] font-bold uppercase tracking-[0.16em] text-primary">
            {t.inRefound}
          </span>

          <div className="mt-3 flex gap-4">
            {example.image ? (
              <img
                src={example.image.src}
                alt={example.image.alt}
                loading="lazy"
                decoding="async"
                width={96}
                height={96}
                className="h-20 w-20 shrink-0 rounded-lg border border-border/60 bg-white object-contain p-1.5 sm:h-24 sm:w-24"
              />
            ) : null}
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">
                {t.identified}
              </p>
              <p className="mt-1 font-semibold leading-snug text-foreground">
                {example.productName}
              </p>
              {example.warrantyBadge ? (
                <span className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                  <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
                  {example.warrantyBadge}
                </span>
              ) : null}
            </div>
          </div>

          {example.details?.length ? (
            <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
              {example.details.map((d, i) => (
                <li key={i}>{d}</li>
              ))}
            </ul>
          ) : null}
          {example.warranty ? (
            <p className="mt-3 flex items-start gap-2 text-sm text-foreground/80">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <span>
                <span className="font-medium">{t.warranty} : </span>
                {example.warranty}
              </span>
            </p>
          ) : null}
          {example.merchantUrl ? (
            <p className="mt-3">
              <a
                href={example.merchantUrl}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary underline-offset-4 hover:underline"
              >
                {example.merchantUrlLabel ?? example.merchantUrl}
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
            </p>
          ) : null}
        </div>
      </div>
    </figure>
  );
}

function Examples({ items }: { items?: ReceiptExample[] }) {
  if (!items?.length) return null;
  return (
    <>
      {items.map((ex, i) => (
        <ReceiptExampleCard key={i} example={ex} />
      ))}
    </>
  );
}

export function Sections({ sections }: { sections: Section[] }) {
  return (
    <div className="space-y-12 md:space-y-14">
      {sections.map((s, i) => (
        <section key={i} id={s.id} className="scroll-mt-28">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground mb-4">
            {s.heading}
          </h2>
          <Prose paragraphs={s.body} />
          <Bullets items={s.bullets} />
          <Examples items={s.examples} />
          <Prose paragraphs={s.afterExamples} />
          {s.sub?.length ? (
            <div className="mt-8 space-y-8">
              {s.sub.map((sub, j) => (
                <div key={j}>
                  <h3 className="font-heading text-xl font-semibold text-foreground mb-3">
                    {sub.heading}
                  </h3>
                  <Prose paragraphs={sub.body} />
                  <Bullets items={sub.bullets} />
                  <Examples items={sub.examples} />
                  <Prose paragraphs={sub.afterExamples} />
                </div>
              ))}
            </div>
          ) : null}
        </section>
      ))}
    </div>
  );
}

export function ComparisonGrid({ data }: { data: Comparison }) {
  return (
    <section className="scroll-mt-28">
      <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground mb-4">
        {data.title}
      </h2>
      {data.intro ? <Prose paragraphs={[data.intro]} /> : null}
      <div className="mt-6 overflow-hidden rounded-2xl border border-border/60 bg-card/40">
        <div className="grid grid-cols-[1.2fr_1fr_1fr] text-sm sm:text-base">
          <div className="bg-muted/40 px-4 py-3 font-semibold text-foreground" />
          <div className="bg-primary/10 px-4 py-3 font-bold text-primary text-center">
            {data.refoundLabel}
          </div>
          <div className="bg-muted/40 px-4 py-3 font-semibold text-muted-foreground text-center">
            {data.othersLabel}
          </div>
          {data.rows.map((row, i) => (
            <Fragment key={i}>
              <div className="border-t border-border/50 px-4 py-3 font-medium text-foreground/90">
                {row.label}
              </div>
              <div className="border-t border-border/50 bg-primary/[0.04] px-4 py-3 text-center text-foreground/90">
                {row.refound}
              </div>
              <div className="border-t border-border/50 px-4 py-3 text-center text-muted-foreground">
                {row.others}
              </div>
            </Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Wide multi-app comparison table. Column 1 (ReFound) is highlighted. */
export function MatrixGrid({ data }: { data: MatrixTable }) {
  return (
    <section className="scroll-mt-28">
      <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground mb-4">
        {data.title}
      </h2>
      {data.intro ? <Prose paragraphs={[data.intro]} /> : null}
      <div className="mt-6 overflow-x-auto rounded-2xl border border-border/60 bg-card/40">
        <table className="w-full min-w-[720px] border-collapse text-sm">
          <thead>
            <tr>
              {data.columns.map((col, i) => (
                <th
                  key={i}
                  scope="col"
                  className={
                    i === 1
                      ? "bg-primary/10 px-4 py-3 text-center font-bold text-primary"
                      : i === 0
                        ? "bg-muted/40 px-4 py-3 text-left font-semibold text-foreground"
                        : "bg-muted/40 px-4 py-3 text-center font-semibold text-muted-foreground"
                  }
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, i) => (
              <tr key={i}>
                <th
                  scope="row"
                  className="border-t border-border/50 px-4 py-3 text-left font-medium text-foreground/90 align-top"
                >
                  {row.label}
                </th>
                {row.cells.map((cell, j) => (
                  <td
                    key={j}
                    className={
                      j === 0
                        ? "border-t border-border/50 bg-primary/[0.04] px-4 py-3 text-center text-foreground/90 align-top"
                        : "border-t border-border/50 px-4 py-3 text-center text-muted-foreground align-top"
                    }
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.note ? (
        <p className="mt-3 text-sm text-muted-foreground/80">
          <InlineText text={data.note} />
        </p>
      ) : null}
    </section>
  );
}

export function FaqAccordion({ title, items }: { title: string; items: FaqQA[] }) {
  return (
    <section id="faq" className="scroll-mt-28">
      <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground mb-6">
        {title}
      </h2>
      <div className="space-y-3">
        {items.map((item, i) => (
          <details
            key={i}
            className="group rounded-xl border border-border/60 bg-card/40 px-5 py-4 [&_summary]:cursor-pointer"
          >
            <summary className="flex items-center justify-between gap-4 list-none font-semibold text-foreground">
              <span>{item.q}</span>
              <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
            </summary>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              <InlineText text={item.a} />
            </p>
          </details>
        ))}
      </div>
    </section>
  );
}

export function RelatedLinks({ title, links }: { title: string; links: LinkRef[] }) {
  if (!links.length) return null;
  return (
    <section>
      <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground mb-5">
        {title}
      </h2>
      <div className="grid gap-3 sm:grid-cols-2">
        {links.map((l, i) => (
          <Link
            key={i}
            to={l.href}
            className="group rounded-xl border border-border/60 bg-card/40 p-4 transition-colors hover:border-primary/40 hover:bg-primary/[0.04]"
          >
            <span className="block font-semibold text-foreground group-hover:text-primary transition-colors">
              {l.label}
            </span>
            {l.desc ? (
              <span className="mt-1 block text-sm text-muted-foreground">
                {l.desc}
              </span>
            ) : null}
          </Link>
        ))}
      </div>
    </section>
  );
}

export function CtaBand({
  title,
  body,
  appStoreUrl,
  freeLabel,
}: {
  title: string;
  body: string;
  appStoreUrl: string;
  freeLabel: string;
}) {
  return (
    <section className="relative overflow-hidden rounded-3xl border border-primary/20 bg-secondary/60 px-6 py-12 text-center sm:px-12">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 60% at 50% 0%, hsl(var(--primary) / 0.12) 0%, transparent 70%)",
        }}
      />
      <div className="relative z-10 mx-auto max-w-2xl">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
          {title}
        </h2>
        <p className="mt-4 text-base sm:text-lg text-muted-foreground">{body}</p>
        <div className="mt-8 flex justify-center">
          <StoreBadges location="cta_band" appStoreUrl={appStoreUrl} />
        </div>
        <p className="mt-4 text-sm text-muted-foreground/70">{freeLabel}</p>
      </div>
    </section>
  );
}

export function Breadcrumbs({ trail }: { trail: { label: string; href?: string }[] }) {
  return (
    <nav aria-label="Breadcrumb" className="mb-6">
      <ol className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
        {trail.map((c, i) => (
          <li key={i} className="flex items-center gap-1.5">
            {c.href ? (
              <Link to={c.href} className="hover:text-primary transition-colors">
                {c.label}
              </Link>
            ) : (
              <span className="text-foreground/80">{c.label}</span>
            )}
            {i < trail.length - 1 ? <span className="opacity-50">/</span> : null}
          </li>
        ))}
      </ol>
    </nav>
  );
}
